import { Navigate, Route, BrowserRouter, Routes, useLocation } from "react-router-dom";
import { CodeGatePage, useCodeGate } from "./modules/auth/CodeGate";
import { SignInPage } from "./modules/auth/SignInPage";
import { BillingReturnPage } from "./modules/billing/BillingReturnPage";
import { AppBackground } from "./modules/chrome/AppBackground";
import { HomePage } from "./modules/workspace/HomePage";
import { OrchestratorPage } from "./modules/workspace/OrchestratorPage";
import { WorkspaceRoomPage } from "./modules/workspace/WorkspaceRoomPage";
import { StatusPage } from "./modules/status/StatusPage";
import { SettingsPage } from "./modules/settings/SettingsPage";
import { UserProfilePage } from "./modules/user_profile/UserProfilePage";
import { InviteAcceptPage } from "./modules/invites/InviteAcceptPage";
import { PublicFindingsPage } from "./modules/public/PublicFindingsPage";
import { getToken } from "./modules/api/client";
import { SpacetimeDBProvider } from "./modules/spacetime/SpacetimeDBContext";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = getToken();
  if (!token) {
    return <Navigate to="/signin" replace />;
  }
  return <SpacetimeDBProvider>{children}</SpacetimeDBProvider>;
}

function AppRoutes() {
  const { passed, unlock } = useCodeGate();
  const location = useLocation();

  // /status and /findings skip both the code gate and the auth guard —
  // these are the public surfaces reachable before the gate unlocks.
  const isPublicPath =
    location.pathname === "/status" || location.pathname === "/findings";
  if (!passed && !isPublicPath) {
    return <CodeGatePage onUnlock={unlock} />;
  }

  return (
    <Routes>
      <Route path="/status" element={<StatusPage />} />
      <Route path="/findings" element={<PublicFindingsPage />} />
      <Route path="/signin" element={<SignInPage />} />
      <Route
        path="/home"
        element={
          <RequireAuth>
            <HomePage />
          </RequireAuth>
        }
      />
      <Route
        path="/orchestrator"
        element={
          <RequireAuth>
            <OrchestratorPage />
          </RequireAuth>
        }
      />
      <Route
        path="/workspaces/:workspaceId"
        element={
          <RequireAuth>
            <WorkspaceRoomPage />
          </RequireAuth>
        }
      />
      <Route
        path="/settings"
        element={
          <RequireAuth>
            <SettingsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/orgs/:orgSlug/settings"
        element={
          <RequireAuth>
            <SettingsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/profile"
        element={
          <RequireAuth>
            <UserProfilePage />
          </RequireAuth>
        }
      />
      <Route path="/invite/:token" element={<InviteAcceptPage />} />
      <Route
        path="/billing/return"
        element={
          <RequireAuth>
            <BillingReturnPage />
          </RequireAuth>
        }
      />
      {/* Root redirect keeps the brand button portable — no page needs to
          know whether the session is live. */}
      <Route
        path="*"
        element={<Navigate to={getToken() ? "/home" : "/signin"} replace />}
      />
    </Routes>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AppBackground>
        <AppRoutes />
      </AppBackground>
    </BrowserRouter>
  );
}
